/* eslint-disable @typescript-eslint/no-explicit-any */

import { CustomErrorType } from '../../models/common/error/custom-error-type/custom-error-type';

export class CustomError {
  private trace = '';
  private errors: string[] = [];
  private tecnicalErrors: string[] = [];
  private publicErrors: string[] = [];
  public status: number;

  constructor(public type: CustomErrorType) {}

  /**
   * Process an exception to CustomError
   * @param trace of your error. Ex "MyUseCase.handler()"
   * @param error to process
   * @returns instance of CustomError
   */
  static handleError(trace: string, error: any): CustomError {
    if (error instanceof CustomError) {
      return error.addTrace(trace);
    } else {
      return CustomError.generic(error).addTrace(trace);
    }
  }

  static generic(error: any = [], status?: number) {
    return new CustomError(CustomErrorType.GENERIC)
      .addTecnicalError(error)
      .addPublicError(error)
      .addStatus(status ?? CustomError.extractStatus(error));
  }

  static invalidContent(errors: string[]): CustomError {
    return new CustomError(CustomErrorType.INVALID_CONTENT).setErrors(errors);
  }

  static notFound(errors: string[]): CustomError {
    return new CustomError(CustomErrorType.NOT_FOUND).setErrors(errors);
  }

  static unauthorized(errors: string[]): CustomError {
    return new CustomError(CustomErrorType.UNAUTHORIZED).setErrors(errors);
  }

  static unauthenticated(errors: string[]): CustomError {
    return new CustomError(CustomErrorType.UNAUTHENTICATED).setErrors(errors);
  }

  addTrace(trace: string): CustomError {
    this.trace = this.trace ? `${trace} -> ${this.trace}` : trace;
    return this;
  }

  setErrors(errors: string[]): CustomError {
    if (errors) this.errors = errors;
    return this;
  }

  addError(error: string): CustomError {
    if (error) this.errors.push(error);
    return this;
  }

  addTecnicalError(error: any): CustomError {
    if (error) {
      if (error instanceof Error) {
        const details = CustomError.extractTechnicalDetails(error);
        if (details) this.tecnicalErrors.push(JSON.stringify(details));
        this.tecnicalErrors.push(error.stack);
      } else {
        this.tecnicalErrors.push(JSON.stringify(error));
      }
    }
    return this;
  }

  addPublicError(error: any): CustomError {
    const errors = CustomError.extractPublicErrors(error);
    if (errors?.length) errors.forEach((e) => this.publicErrors.push(e));
    return this;
  }

  addStatus(status: number): CustomError {
    if (status) this.status = status;
    return this;
  }

  private static extractPublicErrors(error: any): string[] {
    if (!error) return [];

    if (Array.isArray(error)) {
      const apiError = error.find(
        (x) =>
          x?.type &&
          (Array.isArray(x?.translations) || Array.isArray(x?.errors)),
      );

      if (!apiError) return [];

      const codes = CustomError.extractTranslationCodes(apiError);
      if (codes.length) return codes;

      return apiError?.errors ?? [];
    }

    const body = error.body ?? error.error ?? error;
    if (body && body !== error) return CustomError.extractPublicErrors(body);

    const codes = CustomError.extractTranslationCodes(error);
    if (codes.length) return codes;

    if (Array.isArray(error.errors)) return error.errors;

    return [];
  }

  private static extractTranslationCodes(error: any): string[] {
    if (!Array.isArray(error?.translations)) return [];

    return error.translations
      .map((translation) => translation?.code)
      .filter((code) => !!code);
  }

  private static extractStatus(error: any): number | undefined {
    return error?.status ?? error?.statusCode ?? error?.body?.status;
  }

  private static extractTechnicalDetails(error: any): object | null {
    const details: Record<string, unknown> = {};

    if (error.status) details['status'] = error.status;
    if (error.type) details['type'] = error.type;
    if (error.errors) details['errors'] = error.errors;
    if (error.body) details['body'] = error.body;

    return Object.keys(details).length ? details : null;
  }

  getErrors() {
    return this.errors;
  }

  getPublicError() {
    return this.publicErrors;
  }

  getTypeWithErros() {
    return {
      type: this.type,
      errors: this.errors,
    };
  }

  getTecnicalError(): string {
    let log = `<<<\nERROR [${this.type}] ${this.trace}`;
    if (this.tecnicalErrors.length) {
      log += '\n' + JSON.stringify(this.tecnicalErrors, null, 2);
    }
    if (this.errors.length) {
      log += '\n' + JSON.stringify(this.errors, null, 2);
    }
    return `${log}\n>>>`;
  }
}
